import { apiGetPaymentConfig, RespApp, RespAppRightsVersion, RespPaymentConfig } from '@vas2t-lowcode/api';
import { showMessage } from '@vas2t-lowcode/utils';
import { ref } from 'vue';
import { IAppPayData } from './my-app';
import { getMyAppEmitter, MyAppEmitterKey } from './my-app.tools';
import { useWAppRights } from './use-app-rights';

// 支付类型，0：应用支付（我的应用续费、升级） 1：应用支付（我的应用增加人数）2：平台支付（工作区）
export enum PAY_TYPE {
  /** 我的应用-续费、升级、购买：0 */
  MyAppPay,
  /** 我的应用-增加人数：1 */
  MyAppMemPay,
  /** 工作区支付：2 */
  WorkspacePay,
  /** 纯工作区支付：3 */
  NewWorkspacePay,
}

/** 获取配置 */
export const useWMyAppPay = () => {
  const { isSinglePayValid, isAppOutOfDate, isPayTrailValid, isPayPlatformValid } = useWAppRights();

  /** 支付配置 */
  const payConfigData = ref<RespPaymentConfig | undefined>();

  const isPayMyApp = ref(true);

  /** 平台付费期 */
  const isPlatformPayValid = ref(false);

  /** 是否应用单购 */
  const isAppSinglePay = ref(false);
  // computed(() => {
  //   return isSinglePayValid(props.appRightsData);
  // });

  /** 是否有效付费期, true->续费 false->新购 */
  const isValidPaidPeriod = ref(false);
  // computed(() => {
  //   return props.appRightsData.is_valid_paid_period;
  // });

  /** 是否过期 */
  const isOutOfDate = ref(false);
  // computed(() => {
  //   return isAppOutOfDate(props.appRightsData);
  // });

  /** 是否有效期 */
  const isTrailValid = ref(false);
  // computed(() => {
  //   return isPayTrailValid(props.appRightsData);
  // });

  /** 获取支付配置 */
  const getPayConfigAsync = () => {
    return apiGetPaymentConfig().then(({ data }) => {
      payConfigData.value = data;
      return data;
    });
  };

  /** 点击立即购买 */
  // eslint-disable-next-line complexity
  const showPayDialog = async (
    appData: RespApp,
    appRightsData: RespAppRightsVersion,
    options: {
      isShowAddMember: boolean;
      isShowPayPlatform: boolean;
      emitterName: MyAppEmitterKey;
      fromComp: string;
    } = {
      isShowAddMember: false,
      isShowPayPlatform: false,
      emitterName: MyAppEmitterKey.MY_APP_EMITTER,
      fromComp: 'myAppProp',
    }
  ) => {
    // 强行失去焦点
    document.body.click();

    // 初始化值
    let isShowAddMember = false;
    let isShowPayPlatform = false;
    let emitterName = MyAppEmitterKey.MY_APP_EMITTER;
    let fromComp = 'myAppProp';
    if (options) {
      'isShowAddMember' in options && (isShowAddMember = options.isShowAddMember);
      'isShowPayPlatform' in options && (isShowPayPlatform = options.isShowPayPlatform);
      'emitterName' in options && (emitterName = options.emitterName);
      'fromComp' in options && (fromComp = options.fromComp);
    }

    // 更新应用权益
    isAppSinglePay.value = !!isSinglePayValid(appRightsData);
    isValidPaidPeriod.value = !!appRightsData.is_valid_paid_period;
    isOutOfDate.value = !!isAppOutOfDate(appRightsData);
    isTrailValid.value = !!isPayTrailValid(appRightsData);
    isPlatformPayValid.value = !!isPayPlatformValid(appRightsData);
    let type = undefined as PAY_TYPE | undefined;
    if (isShowAddMember) {
      if (isPlatformPayValid.value) {
        // 只要平台有效期内点击添加成员 则显示新增成员弹窗
        type = PAY_TYPE.MyAppMemPay;
      } else if (isAppSinglePay.value) {
        // 纯工作区支付
        type = PAY_TYPE.NewWorkspacePay;
      }
    } else if (!isOutOfDate.value && isTrailValid.value) {
      // 试用期内
      type = PAY_TYPE.MyAppPay;
    } else if (isOutOfDate.value) {
      // 试用到期或者购买到期
      type = PAY_TYPE.MyAppPay;
    } else if (isShowPayPlatform && !isOutOfDate.value) {
      // 平台付费期续费（含同时购买应用版）
      type = PAY_TYPE.WorkspacePay;
    } else if (isAppSinglePay.value) {
      // 应用单购期内
      type = PAY_TYPE.MyAppPay;
    }

    if (type === undefined) {
      showMessage({ msgContent: '支付失败，请稍后重试！', msgType: 'error' });
      console.warn('支付失败，无法获取支付类型');

      return;
    }

    // 显示2t公共支付弹窗
    await getPayConfigAsync();
    let payConfig;
    switch (type) {
      case PAY_TYPE.MyAppMemPay: // 增加人数
        payConfig = payConfigData.value?.pure_team_mode;
        break;
      case PAY_TYPE.MyAppPay: // 应用支付
        payConfig = payConfigData.value?.mix_team_app_mode;
        break;
      case PAY_TYPE.WorkspacePay: // 工作区支付
        payConfig = payConfigData.value?.mix_team_app_mode;
        break;
      case PAY_TYPE.NewWorkspacePay: // 纯工作区支付
        payConfig = payConfigData.value?.pure_team_mode;
        break;
      default:
        break;
    }

    if (!payConfig) {
      console.warn('支付失败', payConfig, type);
      return;
    }

    // 是否支付应用
    const isPayApp = [PAY_TYPE.WorkspacePay, PAY_TYPE.MyAppMemPay].indexOf(type) === -1;

    // 调起支付
    const params = { isPayApp, payConfig, appData, appRightsData, payType: type, fromComp } as IAppPayData;

    const emitter = getMyAppEmitter(emitterName);
    emitter.emit('2t-pay-show', params);
  };

  return {
    isPayMyApp,
    payConfig: payConfigData,
    getPayConfigAsync,
    showPayDialog,
  };
};
